/**
 * Integrations row on the Binom Advanced sub-tab. Mirrors the panel's
 * "Integrations" block — a Magic checker toggle and an Adspect toggle.
 *
 * Each toggle reads + writes its own per-campaign endpoint. Saves are
 * fire-and-forget on toggle, matching panel UX.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { Switch } from '@/components/ui/form/controls/Switch';
import {
  useBinomAdspect,
  useBinomMagicChecker,
  useUpdateBinomAdspect,
  useUpdateBinomMagicChecker,
} from '@/hooks/binom';

import BinomCampaignFormRow from '../campaign/BinomCampaignFormRow';

export interface BinomIntegrationsSectionProps {
  campaignId: string | undefined;
}

const BinomIntegrationsSection: FC<BinomIntegrationsSectionProps> = ({
  campaignId,
}) => {
  const magicQ = useBinomMagicChecker(campaignId);
  const adspectQ = useBinomAdspect(campaignId);
  const updateMagic = useUpdateBinomMagicChecker(campaignId);
  const updateAdspect = useUpdateBinomAdspect(campaignId);

  return (
    <section className="space-y-2">
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Integrations
      </h4>
      <BinomCampaignFormRow label="Magic checker">
        <Switch
          checked={Boolean(magicQ.data?.isEnabled)}
          disabled={!campaignId || magicQ.isLoading || updateMagic.isPending}
          onCheckedChange={(v) =>
            updateMagic.mutate({
              isEnabled: Boolean(v),
              magicCheckerId: magicQ.data?.magicCheckerId ?? null,
              binomMagicCheckerId: magicQ.data?.binomMagicCheckerId ?? null,
              name: magicQ.data?.name ?? null,
            })
          }
          aria-label="Magic checker"
        />
      </BinomCampaignFormRow>
      <BinomCampaignFormRow label="Adspect">
        <Switch
          checked={Boolean(adspectQ.data?.enabled)}
          disabled={!campaignId || adspectQ.isLoading || updateAdspect.isPending}
          onCheckedChange={(v) =>
            updateAdspect.mutate({
              enabled: Boolean(v),
              enabled_tags: adspectQ.data?.enabled_tags ?? [],
              list: adspectQ.data?.list ?? [],
              used_fingerprint: adspectQ.data?.used_fingerprint ?? false,
              white_list_enabled:
                adspectQ.data?.white_list_enabled ?? false,
            })
          }
          aria-label="Adspect"
        />
      </BinomCampaignFormRow>
    </section>
  );
};

export default BinomIntegrationsSection;
